import {RecordHistory} from './OperationsPages';
import React, { useState } from 'react';
import NotesPanel from './NotesPanel';
import ProjectModal from './ProjectModal';
import {
  projectPaymentSummary, invoiceStatusStyle, effectiveDueDate, daysOverdue,
} from '../paymentsUtil';

function statusBadge(status) {
  const map = { Active: 'badge-active', Completed: 'badge-completed', Pending: 'badge-pending', Rejected: 'badge-rejected', Disputed: 'badge-disputed' };
  return <span className={`badge ${map[status] || ''}`}>{status}</span>;
}

const fmt = n => n != null && n !== '' ? `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 0 })}` : '—';
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const SERVICE_LABELS = {
  CMP: 'CMP — Construction Management Plan',
  CEMP: 'CEMP — Construction Environmental Management Plan',
  CDM: 'CDM — Construction Design & Management',
  CLP: 'CLP — Construction Logistics Plan',
  CMS: 'CMS — Construction Method Statement',
  CTMP: 'CTMP — Construction Traffic Management Plan',
  CLMP: 'CLMP — Construction Logistics & Management Plan',
  DCMP: 'DCMP — Demolition & Construction Management Plan',
};

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ width: 160, fontSize: 12, color: 'var(--text3)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text)' }}>{value || '—'}</span>
    </div>
  );
}

export default function ProjectDetailModal({ project, customers, onClose, onRefresh, user }) {
  const canEdit=['owner','operations'].includes(user?.app_metadata?.role);
  const [editing, setEditing] = useState(false);
  const customer = customers.find(c => c.id === project.customer_id);
  const fin = project.financials?.[0];

  if (editing) return (
    <ProjectModal
      project={project}
      customers={customers}
      onClose={() => setEditing(false)}
      onSaved={() => { setEditing(false); onRefresh(); }}
      user={user}
    />
  );

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 680 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', align: 'center', gap: 12 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--accent)' }}>{project.project_code}</span>
            <h2 style={{ fontSize: 15 }}>{project.project_name}</h2>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {user?.id==='6870263b-7758-4486-b372-d7fda5c998b3'&&<button className="btn-ghost" onClick={()=>{window.dispatchEvent(new CustomEvent('invoice:project',{detail:project.id}));onClose();}}>Faturalar</button>}
            {canEdit&&<button className="btn-ghost" onClick={()=>{window.dispatchEvent(new CustomEvent('publication:project',{detail:project.id}));onClose();}}>Yayınlar</button>}
            {canEdit&&<button className="btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => setEditing(true)}>Edit</button>}
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            {statusBadge(project.status)}
            <span style={{ fontSize: 12, background: 'var(--bg3)', padding: '3px 10px', borderRadius: 4, color: 'var(--text2)' }}>
              {project.service_type}
            </span>
          </div>

          <div style={{ marginBottom: 20 }}>
            <Row label="Customer" value={customer ? `${customer.full_name}${customer.company ? ` — ${customer.company}` : ''}` : null} />
            <Row label="Council" value={project.council} />
            <Row label="Planning Ref" value={project.planning_ref} />
            <Row label="Order Date" value={fmtDate(project.order_date)} />
            <Row label="Delivery Date" value={fmtDate(project.delivery_date)} />
            <Row label="Service line" value={project.service_line?.replaceAll('_',' ')} />
            <RecordHistory table="projects" id={project.id}/>
            <Row label="Created By" value={project.created_by_name} />
          </div>

          {canEdit&&<>
          <label style={{ fontSize: 12, color: 'var(--accent)', display: 'block', marginBottom: 10 }}>FINANCIALS</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Tender Fee', val: fmt(fin?.tender_fee) },
              { label: 'Agreed Fee', val: fmt(fin?.agreed_fee) },
              { label: 'Paid', val: fmt(fin?.paid_amount), color: 'var(--green)' },
              { label: 'Pending', val: fmt(fin?.pending_amount), color: fin?.pending_amount > 0 ? 'var(--red)' : undefined },
            ].map(s => (
              <div key={s.label} style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 12px' }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: s.color || 'var(--text)' }}>{s.val}</div>
              </div>
            ))}
          </div>

          {(() => {
            const s = projectPaymentSummary(project);
            if (!s.hasFinancials) return null;
            const renderInv = (label, inv) => {
              if (inv.status === 'not-issued' && !inv.no && !inv.date) return null;
              const style = invoiceStatusStyle(inv.status);
              const dueShown = effectiveDueDate(inv.date, inv.due);
              const extra = inv.status === 'overdue'
                ? `${daysOverdue(dueShown)}d overdue`
                : inv.status === 'pending' && dueShown
                  ? `due ${fmtDate(dueShown)}`
                  : inv.status === 'paid'
                    ? `paid ${fmt(inv.paid)}`
                    : '';
              return (
                <Row
                  key={label}
                  label={label}
                  value={
                    <span>
                      {inv.status==='not-required'?'No second invoice':inv.no || 'No invoice info'}
                      {inv.date && <span style={{ color: 'var(--text3)' }}> · {fmtDate(inv.date)}</span>}
                      <span style={{
                        marginLeft: 10, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                        color: style.color, background: style.bg, padding: '2px 7px', borderRadius: 3,
                      }}>{style.label.toUpperCase()}</span>
                      {extra && <span style={{ marginLeft: 8, color: 'var(--text3)', fontSize: 12 }}>{extra}</span>}
                    </span>
                  }
                />
              );
            };
            return (
              <div style={{ marginBottom: 16 }}>
                {renderInv('Invoice 1', s.inv1)}
                {renderInv('Invoice 2', s.inv2)}
                {fin?.payment_notes && <Row label="Payment Notes" value={fin.payment_notes} />}
              </div>
            );
          })()}

          </>}
          <hr className="divider" />
          <NotesPanel projectId={project.id} user={user} />
        </div>
      </div>
    </div>
  );
}
